import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { useCallback, useEffect, useRef, useState } from 'react'
import { gte } from 'semver'

export type VideoAutoplayMode = 'always' | 'wifiOnly' | 'off'

const MINIMUM_VERSION = '11.7.0'

function isWifiApiSupported() {
  return TmapApp.env.isInApp && gte(TmapApp.env.appVersion, MINIMUM_VERSION)
}

function computeCanAutoplay(mode: VideoAutoplayMode, isWifi: boolean) {
  return mode === 'always' || (mode === 'wifiOnly' && isWifi)
}

/**
 * 숏폼/동영상 리뷰 프리뷰 자동 재생 여부를 네이티브 설정값과 Wi-Fi 상태에 따라 판단.
 * 11.7.0 미만에서는 항상 자동재생(always)으로 동작.
 *
 * mode/isWifiConnected 각각의 변경이 서로의 최신값을 ref로 참조하여
 * 비동기 응답 순서에 관계없이 항상 정확한 canAutoplay를 계산합니다.
 *
 * @since Android/iOS 11.7.0
 */
export function useVideoAutoplay() {
  const [canAutoplay, setCanAutoplay] = useState(true)
  const [mode, setMode] = useState<VideoAutoplayMode>('always')
  const [isWifiConnected, setIsWifiConnected] = useState(true)
  const [resumeKey, setResumeKey] = useState(0)
  const modeRef = useRef<VideoAutoplayMode>('always')
  const wifiRef = useRef(true)

  const notifyAutoplayResume = useCallback(() => {
    setResumeKey((k) => k + 1)
  }, [])

  const updateCanAutoplay = useCallback((nextMode: VideoAutoplayMode, nextWifi: boolean) => {
    modeRef.current = nextMode
    wifiRef.current = nextWifi
    setCanAutoplay(computeCanAutoplay(nextMode, nextWifi))
  }, [])

  const fetchWifiStatus = useCallback(async () => {
    if (!isWifiApiSupported()) return
    try {
      const result = await TmapApp.getWifiConnected()
      console.log('getWifiConnected result', result)
      wifiRef.current = result.result
      setIsWifiConnected(result.result)
      updateCanAutoplay(modeRef.current, result.result)
    } catch {
      wifiRef.current = true
      setIsWifiConnected(true)
      updateCanAutoplay(modeRef.current, true)
    }
  }, [updateCanAutoplay])

  const fetchSetting = useCallback(async () => {
    if (!isWifiApiSupported()) {
      modeRef.current = 'always'
      setMode('always')
      updateCanAutoplay('always', wifiRef.current)
      return
    }
    try {
      const value = await TmapApp.getUserSetting({ key: 'feature.videoPreviewAutoplayMode' })
      console.log('videoPreviewAutoplayMode', value)
      const parsed = (value || 'always') as VideoAutoplayMode
      if (parsed === 'always' || parsed === 'wifiOnly' || parsed === 'off') {
        modeRef.current = parsed
        setMode(parsed)
        updateCanAutoplay(parsed, wifiRef.current)
      } else {
        modeRef.current = 'always'
        setMode('always')
        updateCanAutoplay('always', wifiRef.current)
      }
    } catch {
      modeRef.current = 'always'
      setMode('always')
      updateCanAutoplay('always', wifiRef.current)
    }
  }, [updateCanAutoplay])

  useEffect(() => {
    fetchSetting()
    fetchWifiStatus()
  }, [fetchSetting, fetchWifiStatus])

  useEffect(() => {
    if (!isWifiApiSupported()) return
    const off = TmapApp.utils.addNativeEventListener('onWifiConnected', (isConnected: boolean) => {
      console.log('onWifiConnected isConnected', isConnected)
      wifiRef.current = isConnected
      setIsWifiConnected(isConnected)
      updateCanAutoplay(modeRef.current, isConnected)
    })
    return off
  }, [updateCanAutoplay])

  const refreshOnResume = useCallback(() => {
    if (!isWifiApiSupported()) return
    fetchSetting().then(() => {
      const latestMode = modeRef.current
      if (latestMode === 'off') return
      if (latestMode === 'always') {
        notifyAutoplayResume()
        return
      }
      fetchWifiStatus().then(() => {
        if (wifiRef.current) {
          notifyAutoplayResume()
        }
      })
    })
  }, [fetchSetting, fetchWifiStatus, notifyAutoplayResume])

  return { canAutoplay, mode, isWifiConnected, resumeKey, refreshOnResume }
}
